import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Loader2, MapPin, ShieldCheck, Trophy, Users } from 'lucide-react';
import { Button } from '@tark/shared/components';
import { useAuth } from '@tark/shared/hooks';
import { getErrorMessage, joinSquadFromInvite, previewSquadInvite } from '@tark/shared/lib';
import BrandMark from '@/components/BrandMark';
import InviteVerification from '@/features/squad-invite/InviteVerification';
import { clearInvite, keepInvite, inviteErrorCode } from '@/features/squad-invite/inviteContinuation';

function validInviteReference(value: string): boolean {
  return /^[A-Za-z0-9_-]{4,128}$/.test(value);
}

function invitationError(error: unknown): string {
  const message = getErrorMessage(error);
  if (/expired|no longer valid|not active/i.test(message)) {
    return 'This invitation has expired or is no longer active.';
  }
  if (/not found|invalid invite/i.test(message)) {
    return 'We could not find this squad invitation.';
  }
  return 'This squad invitation could not be loaded. Try again.';
}

export default function SquadInvitePage() {
  const { user, isLoading } = useAuth();
  const { codeOrToken = '' } = useParams();
  if (isLoading) return <div role="status">Loading invitation...</div>;
  return <SquadInviteContent key={`${user?.id ?? 'guest'}:${codeOrToken}`} />;
}

function SquadInviteContent() {
  const navigate = useNavigate();
  const { codeOrToken = '' } = useParams();
  const { isAuthenticated, isLoading: isAuthLoading, user, refetch: refreshAuth } = useAuth();
  const queryClient = useQueryClient();
  const [actionError, setActionError] = useState<string | null>(null);
  const [failure, setFailure] = useState<unknown>(null);
  const mounted = useRef(true);
  const mutationBusy = useRef(false);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const inviteReference = useMemo(() => codeOrToken.trim(), [codeOrToken]);
  const returnPath = `/join/${encodeURIComponent(inviteReference)}`;
  const [intentReady, setIntentReady] = useState(() => keepInvite(inviteReference, user?.id != null ? String(user.id) : null));
  const request = { expectedUserId: user?.id != null ? String(user.id) : undefined,
    assertRequestCurrent: () => {
      if (!mounted.current || window.location.pathname !== returnPath) throw new Error('Invitation account or route changed');
    },
  };
  const accountReady = Boolean(
    isAuthenticated && (!user?.nextStep || ['TABS', 'WELCOME'].includes(user.nextStep))
  );

  const previewQuery = useQuery({
    queryKey: ['squad-invite-preview', inviteReference, user?.id ?? 'guest'],
    queryFn: ({ signal }) => previewSquadInvite(inviteReference, { ...request, signal }),
    enabled: validInviteReference(inviteReference) && intentReady && !isAuthLoading,
    retry: false,
  });

  const joinMutation = useMutation({
    mutationFn: async (openOnly: boolean) => {
      if (!keepInvite(inviteReference, user?.id != null ? String(user.id) : null)) {
        setIntentReady(false);
        throw new Error('Saved invitation unavailable. Reopen the original link.');
      }
      const fresh = await previewSquadInvite(inviteReference, request);
      if (!mounted.current) throw new Error('Account changed');
      if (fresh.isMember) return fresh;
      if (openOnly) return { ...fresh, needsJoinConfirmation: true as const };
      try { return await joinSquadFromInvite(inviteReference, request); }
      catch (error) {
        if (mounted.current && inviteErrorCode(error) === 'ALREADY_SQUAD_MEMBER'
          && (error as { response?: { status?: number } })?.response?.status === 409) {
          const confirmed = await previewSquadInvite(inviteReference, request);
          if (confirmed.isMember) return confirmed;
        }
        throw error;
      }
    },
    onSuccess: (squad) => {
      if (!mounted.current) return;
      if ('needsJoinConfirmation' in squad) {
        queryClient.setQueryData(['squad-invite-preview', inviteReference, user?.id ?? 'guest'], squad);
        setActionError('Your membership changed. Review the invitation and choose Join squad to rejoin.');
        return;
      }
      clearInvite(inviteReference);
      navigate(`/squads/${squad.id}`, { replace: true });
    },
    onError: (error) => {
      if (!mounted.current) return;
      setFailure(error);
      const code = inviteErrorCode(error);
      setActionError(code === 'UNVERIFIED_EMAIL' ? 'Verify your email to continue.'
        : code === 'UNVERIFIED_PHONE' ? 'Verify your phone to continue.'
        : code === 'ACCOUNT_CHANGED' ? 'Your signed-in account changed. Reload this invitation.'
        : 'Could not join this squad. Complete any required setup below, or preview again and contact the squad admin.');
    },
    onSettled: () => { mutationBusy.current = false; },
  });
  const shownFailure = failure ?? previewQuery.error;
  const errorCode = inviteErrorCode(shownFailure);
  const setup = (shownFailure as { response?: { data?: { error?: string; code?: string; nextStep?: string } } })?.response?.data;
  const setupRecovery = setup?.error === 'ACCOUNT_SETUP_REQUIRED' && setup.code !== 'MINOR_RESTRICTED' && setup.nextStep !== 'MINOR_RESTRICTED';
  const verificationChannel = errorCode === 'UNVERIFIED_EMAIL' ? 'email' : errorCode === 'UNVERIFIED_PHONE' ? 'phone' : null;
  const continueSetup = async (event: React.MouseEvent) => {
    event.preventDefault();
    const result = await refreshAuth();
    if (!mounted.current) return;
    if (result.isError || !result.data || String(result.data.id) !== request.expectedUserId
      || !result.data.nextStep || ['TABS', 'WELCOME'].includes(result.data.nextStep)) {
      setActionError('Could not refresh account setup. Try again.');
      return;
    }
    navigate(`/register?return=${encodeURIComponent(returnPath)}`);
  };
  const recoveryPanel = verificationChannel && isAuthenticated ? <InviteVerification key={verificationChannel} channel={verificationChannel} expectedUserId={String(user!.id)}
    onChecked={async () => { const result = await previewQuery.refetch(); if (mounted.current && !result.isError) { setActionError(null); setFailure(null); } }} />
    : setupRecovery ? <Link className="mt-4 inline-flex font-semibold text-primary" onClick={event => void continueSetup(event)} to={`/register?return=${encodeURIComponent(returnPath)}`}>Continue account setup</Link> : null;

  const preview = previewQuery.data;
  const initials = preview?.name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('') || 'T';

  if (!validInviteReference(inviteReference)) {
    return <InviteState title="Invalid invitation" message="Ask the squad admin for a new link." />;
  }
  if (!intentReady) return <InviteState title="Saved invitation unavailable" message="This saved invitation timed out or belongs to another account. Reopen the original link to start again."
    recovery={<Button onClick={() => { clearInvite(inviteReference); window.location.reload(); }}>Use this invitation with this account</Button>} />;

  if (previewQuery.isLoading || isAuthLoading) {
    return (
      <div className="public-theme flex min-h-screen items-center justify-center bg-background text-text">
        <Loader2 className="h-8 w-8 animate-spin text-primary" aria-label="Loading squad invitation" />
      </div>
    );
  }

  if (previewQuery.isError || !preview) {
    return <InviteState title={recoveryPanel ? 'Complete account setup' : 'Invitation unavailable'}
      message={actionError ?? (recoveryPanel ? 'Your invitation is kept while you complete this step.' : invitationError(previewQuery.error))}
      recovery={recoveryPanel ?? <Button onClick={() => void previewQuery.refetch()}>Retry preview</Button>} />;
  }

  const primaryAction = () => {
    if (mutationBusy.current) return;
    mutationBusy.current = true;
    setActionError(null);
    joinMutation.mutate(preview.isMember === true);
  };

  return (
    <div className="public-theme min-h-screen bg-background text-text">
      <div className="mx-auto flex min-h-screen w-full max-w-2xl flex-col px-4 py-5 sm:px-6 sm:py-7">
        <header className="flex items-center justify-between border-b border-border pb-4">
          <BrandMark size="sm" titleClassName="text-lg" />
          {accountReady ? (
            <Link to="/squads" onClick={() => { mounted.current = false; }} className="text-sm font-semibold text-primary hover:underline">
              My squads
            </Link>
          ) : null}
        </header>

        <main className="flex flex-1 items-center py-8 sm:py-12">
          <section className="w-full" aria-labelledby="squad-invite-title">
            <div className="flex items-start gap-4">
              <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-primary text-xl font-bold [color:var(--color-on-primary)]" aria-hidden>
                {initials}
              </div>
              <div className="min-w-0 pt-1">
                <p className="text-sm font-semibold text-primary">Squad invitation</p>
                <h1 id="squad-invite-title" className="mt-1 break-words text-[28px] font-bold leading-tight sm:text-[32px]">
                  {preview.name}
                </h1>
                {preview.createdByName ? (
                  <p className="mt-1 text-sm text-text-muted">Organized by {preview.createdByName}</p>
                ) : null}
              </div>
            </div>

            {preview.description ? (
              <p className="mt-6 max-w-xl text-base leading-relaxed text-text-muted">{preview.description}</p>
            ) : null}

            <div className="mt-6 flex flex-wrap gap-x-6 gap-y-3 border-y border-border py-4 text-sm text-text-muted">
              <span className="inline-flex items-center gap-2">
                <Users className="h-4 w-4 text-primary" />
                {preview.memberCount} {preview.memberCount === 1 ? 'member' : 'members'}
              </span>
              {preview.sport ? (
                <span className="inline-flex items-center gap-2">
                  <Trophy className="h-4 w-4 text-primary" />
                  {preview.sport.charAt(0).toUpperCase() + preview.sport.slice(1).toLowerCase()}
                </span>
              ) : null}
              {preview.location ? (
                <span className="inline-flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-primary" />
                  {preview.location}
                </span>
              ) : null}
            </div>

            <div className="mt-7">
              {accountReady ? (
                <Button
                  size="xl"
                  onClick={primaryAction}
                  isLoading={joinMutation.isPending}
                  rightIcon={preview.isMember ? <ArrowRight /> : <CheckCircle2 />}
                >
                  {preview.isMember ? 'Open squad' : 'Join squad'}
                </Button>
              ) : isAuthenticated ? (
                <Link
                  to={`/register?return=${encodeURIComponent(returnPath)}`}
                  className="inline-flex h-11 items-center gap-2 rounded-lg bg-primary px-6 text-base font-semibold [color:var(--color-on-primary)]"
                >
                  Finish account setup <ArrowRight className="h-5 w-5" />
                </Link>
              ) : (
                <div className="flex flex-col gap-3 sm:flex-row">
                  <Link
                    to={`/register?return=${encodeURIComponent(returnPath)}`}
                    className="inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-primary px-6 text-base font-semibold [color:var(--color-on-primary)]"
                  >
                    Create account <ArrowRight className="h-5 w-5" />
                  </Link>
                  <Link
                    to={`/login?return=${encodeURIComponent(returnPath)}`}
                    className="inline-flex h-11 items-center justify-center rounded-lg border border-border px-6 text-base font-semibold text-text hover:bg-surface"
                  >
                    Sign in
                  </Link>
                </div>
              )}

              {actionError ? (
                <p className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
                  {actionError}
                </p>
              ) : null}
              {recoveryPanel}
              <Link to="/squads" onClick={() => { mounted.current = false; clearInvite(inviteReference); }} className="mt-4 block text-sm text-text-muted">Cancel invitation</Link>
            </div>

            <p className="mt-7 flex max-w-xl items-start gap-2 text-sm leading-relaxed text-text-muted">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              Joining gives you access to this squad's member space. Your verified TARK account remains yours.
            </p>
          </section>
        </main>
      </div>
    </div>
  );
}

function InviteState({ title, message, recovery }: { title: string; message: string; recovery?: React.ReactNode }) {
  return (
    <div className="public-theme flex min-h-screen items-center justify-center bg-background px-4 text-text">
      <main className="w-full max-w-md text-center">
        <BrandMark size="sm" titleClassName="text-lg" />
        <h1 className="mt-8 text-2xl font-bold">{title}</h1>
        <p className="mt-3 text-text-muted">{message}</p>
        {recovery}
        <Link to="/" className="mt-6 inline-flex text-sm font-semibold text-primary hover:underline">
          Go to TARK
        </Link>
      </main>
    </div>
  );
}
